/**
 * PURPOSE: Edición inline de fecha, hora, sede y cancha en el Centro de Programación (FTR-0028 D-24).
 * RESPONSIBILITIES: Cuatro controles reutilizables por las cuatro vistas, con marca de "sin guardar"
 *   y la relación sede→cancha aplicada en el propio control.
 *
 *   NO SE PROGRAMA UN BYE (D-20). Cuando la fila no es programable los controles no se deshabilitan:
 *   no se renderizan. Un campo gris invita a intentarlo; su ausencia explica que no aplica.
 *
 *   LA CANCHA DEPENDE DE LA SEDE. Solo se ofrecen canchas ACTIVAS de la sede elegida —lo mismo que
 *   acepta el backend— y una cancha retirada que ya está asignada se sigue mostrando, marcada, para
 *   que un partido histórico no pierda su ubicación en pantalla.
 * COLLABORATORS: MatchRow.jsx, VenueView.jsx, DateView.jsx, utils/programmingCenterEdit.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/ScheduleCells.jsx
 */
import React from "react";

import { isScheduleFieldDirty } from "../../../../utils/programmingCenterEdit.js";

const baseInput =
  "w-full rounded-lg border px-2 py-1 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400";

const dirtyClass = (dirty) =>
  dirty ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-white";

export const DateCell = ({ row, entry, onChange, disabled = false }) => {
  if (!row.canSchedule) return <span className="text-xs text-slate-400">—</span>;
  const dirty = isScheduleFieldDirty(row, entry, "date");
  return (
    <input
      type="date"
      aria-label={`Fecha de ${row.label}`}
      value={entry?.date ?? ""}
      disabled={disabled}
      onChange={(event) => onChange("date", event.target.value)}
      className={`${baseInput} ${dirtyClass(dirty)}`}
    />
  );
};

export const TimeCell = ({ row, entry, onChange, disabled = false }) => {
  if (!row.canSchedule) return <span className="text-xs text-slate-400">—</span>;
  const dirty = isScheduleFieldDirty(row, entry, "time");
  return (
    <input
      type="time"
      aria-label={`Hora de ${row.label}`}
      value={entry?.time ?? ""}
      disabled={disabled}
      onChange={(event) => onChange("time", event.target.value)}
      className={`${baseInput} ${dirtyClass(dirty)}`}
    />
  );
};

export const VenueCell = ({ row, entry, venues = [], onChange, disabled = false }) => {
  if (!row.canSchedule) return <span className="text-xs text-slate-400">—</span>;
  const dirty = isScheduleFieldDirty(row, entry, "idsede");
  return (
    <select
      aria-label={`Sede de ${row.label}`}
      value={entry?.idsede ?? ""}
      disabled={disabled}
      onChange={(event) => onChange("idsede", event.target.value)}
      className={`${baseInput} ${dirtyClass(dirty)}`}
    >
      <option value="">Sin sede</option>
      {venues.map((venue) => (
        <option key={venue.idsede} value={venue.idsede}>
          {venue.name}
        </option>
      ))}
    </select>
  );
};

export const CourtCell = ({ row, entry, courtsByVenue, onChange, disabled = false }) => {
  if (!row.canSchedule) return <span className="text-xs text-slate-400">—</span>;
  const idsede = Number(entry?.idsede) || null;
  const courts = idsede ? courtsByVenue.get(idsede) || [] : [];
  const selected = Number(entry?.idcancha) || null;
  const active = courts.filter((court) => Number(court.state) === 1);
  // Una cancha retirada que YA está asignada se mantiene en la lista: el partido no debe perder su
  // ubicación en pantalla solo porque la cancha dejó de ofrecerse.
  const retainedRetired = courts.find(
    (court) => Number(court.idcancha) === selected && Number(court.state) !== 1
  );
  const dirty = isScheduleFieldDirty(row, entry, "idcancha");

  return (
    <select
      aria-label={`Cancha de ${row.label}`}
      value={entry?.idcancha ?? ""}
      disabled={disabled || !idsede}
      onChange={(event) => onChange("idcancha", event.target.value)}
      title={!idsede ? "Elige primero una sede" : undefined}
      className={`${baseInput} ${dirtyClass(dirty)}`}
    >
      <option value="">{idsede ? "Sin cancha" : "Elige una sede"}</option>
      {active.map((court) => (
        <option key={court.idcancha} value={court.idcancha}>
          {court.name}
        </option>
      ))}
      {retainedRetired ? (
        <option value={retainedRetired.idcancha}>{retainedRetired.name} (retirada)</option>
      ) : null}
    </select>
  );
};

/** Los cuatro campos en fila. Es el bloque que comparten la vista Por Fecha y la vista Por Sede. */
const ScheduleCells = ({ row, entry, venues, courtsByVenue, onChange, disabled = false }) => (
  <>
    <DateCell row={row} entry={entry} onChange={onChange} disabled={disabled} />
    <TimeCell row={row} entry={entry} onChange={onChange} disabled={disabled} />
    <VenueCell row={row} entry={entry} venues={venues} onChange={onChange} disabled={disabled} />
    <CourtCell
      row={row}
      entry={entry}
      courtsByVenue={courtsByVenue}
      onChange={onChange}
      disabled={disabled}
    />
  </>
);

export default ScheduleCells;
